import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@/features/auth'
import { GuidedIngestionDialog } from './GuidedIngestionDialog'

/**
 * STEP 7.10 — Guided ingestion provider.
 *
 * Holds the open/closed state of the upload-from-prompt dialog and exposes
 * a single ``openWithFile(file)`` entry point. Composer surfaces (home and
 * active chat) wire their "Anexar" button to a hidden ``<input type="file">``
 * and forward the picked file to this provider — the dialog then owns the
 * rest of the flow (metadata, validation, duplicate check, upload, scope
 * selection, new-consultation hand-off).
 *
 * Security/auth model is inherited from the rest of frontend-chat:
 *   - tenant_id is read from the authenticated organization, never typed
 *     by the user nor persisted to storage;
 *   - the dialog is only mounted while a tenant is available, so the file
 *     reference is dropped if the session expires mid-flow.
 *
 * Lifetime: the file lives in React state for the duration of the dialog.
 * On close (success, cancel, error-then-cancel) the reference is released
 * so the browser can GC the underlying blob.
 */

interface GuidedIngestionContextValue {
  /** True while the modal is mounted. */
  isOpen: boolean
  /** Open the dialog with a freshly-picked file. */
  openWithFile: (file: File) => void
  /** Force the dialog closed — used by the dialog itself after success/cancel. */
  close: () => void
}

const GuidedIngestionContext = createContext<GuidedIngestionContextValue | null>(null)

interface ProviderProps {
  children: ReactNode
}

export function GuidedIngestionProvider({ children }: ProviderProps) {
  const { organization } = useAuth()
  const [file, setFile] = useState<File | null>(null)

  const openWithFile = useCallback((next: File) => {
    setFile(next)
  }, [])

  const close = useCallback(() => {
    setFile(null)
  }, [])

  const value = useMemo<GuidedIngestionContextValue>(
    () => ({
      isOpen: file !== null,
      openWithFile,
      close,
    }),
    [file, openWithFile, close],
  )

  return (
    <GuidedIngestionContext.Provider value={value}>
      {children}
      {/* Only mount the dialog when there's an authenticated organization —
          otherwise scope/conversations providers cannot do anything sensible
          and the user shouldn't see the modal in the first place. */}
      {file && organization ? (
        <GuidedIngestionDialog file={file} onClose={close} />
      ) : null}
    </GuidedIngestionContext.Provider>
  )
}

export function useGuidedIngestion(): GuidedIngestionContextValue {
  const ctx = useContext(GuidedIngestionContext)
  if (!ctx) {
    throw new Error(
      'useGuidedIngestion must be used inside <GuidedIngestionProvider>',
    )
  }
  return ctx
}
